import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { reportRegistration } from '../lib/pixel';
import { takeReturnTo } from '../lib/returnTo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle, Briefcase, Home } from 'lucide-react';
import { SkillsSelector } from '../components/profile';
import { WORKER_SKILLS, BOHOL_TOWNS } from '../lib/constants';
import { ResumeUploadCard } from '../components/ResumeUploadCard';

/**
 * Profile Completion Page - Progressive Profiling Step 2
 * Collects essential information after role selection (for Google OAuth users)
 * Fields match the regular email signup form exactly
 */
const ProfileCompletion = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState('/logo-transparent.png');
  const [profile, setProfile] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [formData, setFormData] = useState({
    town: '', // required for everyone (seekers + employers)
    // Job seeker fields
    phone: '',
    education_level: '',
    years_of_experience: '',
    date_of_birth: '',
    skills: [],
    // Employer fields
    is_private_household: false,
    business_name: '',
    business_type: '',
    industry: '',
    business_phone: '',
    number_of_employees: '',
    facebook_page: '',
  });
  const [termsAgreed, setTermsAgreed] = useState(false);

  // Run initialization only once on mount
  useEffect(() => {
    checkUserProfile();
    fetchLogo();
  }, []);

  // CRITICAL: Safety timeout - separate effect to prevent re-calling init functions
  useEffect(() => {
    // Only set timeout if not yet initialized
    if (initialized) return;

    const safetyTimer = setTimeout(() => {
      console.error('[ProfileCompletion] Safety timeout - initialization took too long');
      window.location.href = '/sign-up';
    }, 10000);

    return () => clearTimeout(safetyTimer);
  }, [initialized]);

  const fetchLogo = async () => {
    try {
      const response = await api.get('/api/settings/version');
      if (response.data.logo_url) {
        setLogoUrl(response.data.logo_url);
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
    }
  };

  const checkUserProfile = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        // Set initialized to prevent safety timeout during redirect
        setInitialized(true);
        window.location.href = '/sign-up';
        return;
      }

      // Get profile - use maybeSingle to handle edge cases gracefully
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('auth_user_id', session.user.id)
        .maybeSingle();

      if (profileError) {
        console.error('[ProfileCompletion] Error fetching profile:', profileError);
      }

      if (!profileData?.role || profileData.role === 'pending') {
        // No role set, redirect to role selection
        // Set initialized to prevent safety timeout during redirect
        setInitialized(true);
        window.location.href = '/role-selection';
        return;
      }

      // PESO/NSRP registrants are already fully captured by the NSRP form — never trap
      // them on profile completion (mirrors App.js isPesoRegistrant bypass).
      if (typeof profileData.registration_source === 'string' && profileData.registration_source.startsWith('peso_')) {
        setInitialized(true);
        window.location.href = '/dashboard';
        return;
      }

      // Check if profile is already complete - must match App.js needsProfileCompletion logic exactly
      const hasPhone = profileData.phone && profileData.phone.trim() !== '';
      const hasTown = profileData.town && profileData.town.trim() !== '';
      const isWorker = profileData.role === 'job_seeker' || profileData.role === 'contractor';
      const isEmployer = profileData.role === 'employer' || profileData.role === 'owner';

      const workerComplete = !isWorker || (profileData.years_of_experience && profileData.education_level && profileData.date_of_birth);
      const employerComplete = !isEmployer || (profileData.business_name && profileData.industry && profileData.number_of_employees) || profileData.is_private_household;

      // Only redirect to dashboard if ALL required fields are complete (town now included,
      // matching App.js needsProfileCompletion — so existing town-less users get prompted).
      if (hasPhone && hasTown && workerComplete && employerComplete) {
        // Profile already completed, go to dashboard
        // Set initialized to prevent safety timeout during redirect
        setInitialized(true);
        window.location.href = '/dashboard';
        return;
      }

      setProfile(profileData);
      setInitialized(true);

      // Pre-fill existing data if any
      if (profileData.terms_agreed_at) setTermsAgreed(true);
      setFormData({
        town: profileData.town || '',
        phone: profileData.phone || profileData.business_phone || '',
        // 'doctorate' is a retired value merged into 'masters' (Post Graduate) — coerce so
        // the Select shows it instead of rendering blank, and re-saves the canonical value.
        education_level: profileData.education_level === 'doctorate' ? 'masters' : (profileData.education_level || ''),
        years_of_experience: profileData.years_of_experience || '',
        date_of_birth: profileData.date_of_birth || '',
        skills: profileData.skills || [],
        is_private_household: profileData.is_private_household || false,
        business_name: profileData.business_name || '',
        business_type: profileData.business_type || '',
        industry: profileData.industry || '',
        business_phone: profileData.business_phone || profileData.phone || '',
        number_of_employees: profileData.number_of_employees || '',
        facebook_page: profileData.social_media_links?.facebook || '',
      });
    } catch (error) {
      console.error('Error checking user profile:', error);
      // Set initialized to prevent safety timeout during redirect
      setInitialized(true);
      window.location.href = '/sign-up';
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Pre-fill the form from a parsed resume. Form fields (phone/years/education/skills)
  // go into local state so the user can review before clicking Continue. Extras the
  // form doesn't capture (town/tagline/work_history/education_field/name) are saved
  // directly via PUT /api/profile so they're not lost.
  const handleResumeParsed = async (extracted) => {
    const formUpdates = {};
    if (extracted.phone) formUpdates.phone = extracted.phone;
    if (extracted.years_of_experience) formUpdates.years_of_experience = extracted.years_of_experience;
    if (extracted.education_level) formUpdates.education_level = extracted.education_level;
    // Town is now a required form field — put it in form state (so it pre-fills, passes
    // validation, and is saved on submit) rather than PUT-ing it as a hidden extra that
    // the submit would then clobber. Only accept it if the resume-extracted value is a real
    // Bohol town (case-insensitive), normalized to the canonical dropdown value — otherwise
    // leave it empty so the required dropdown stays honest (Radix Select shows a blank
    // placeholder for a value not in its items, which would look "unselected" yet submit).
    if (extracted.town) {
      const matchedTown = BOHOL_TOWNS.find((tn) => tn.toLowerCase() === String(extracted.town).trim().toLowerCase());
      if (matchedTown) formUpdates.town = matchedTown;
    }
    if (Array.isArray(extracted.skills) && extracted.skills.length > 0) {
      formUpdates.skills = extracted.skills.slice(0, 12);
    }
    if (Object.keys(formUpdates).length > 0) {
      setFormData(prev => ({ ...prev, ...formUpdates }));
    }

    const extras = {};
    if (extracted.name && !profile?.name) extras.name = extracted.name;
    if (extracted.tagline && extracted.tagline.length <= 80) extras.tagline = extracted.tagline;
    if (extracted.education_field) extras.education_field = extracted.education_field;
    if (Array.isArray(extracted.work_history) && extracted.work_history.length > 0) {
      extras.work_history = extracted.work_history.slice(0, 5);
    }
    if (Object.keys(extras).length === 0) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (token) {
        await api.put('/api/profile', extras, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (err) {
      console.warn('[ProfileCompletion] Failed to save resume extras (non-fatal):', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isWorker = profile.role === 'job_seeker' || profile.role === 'contractor';
    const isEmployer = profile.role === 'employer' || profile.role === 'owner';

    // Require terms agreement
    if (!termsAgreed) {
      toast.error(t('auth.pleaseAgreeToTerms', 'Please agree to the Privacy Policy and Terms of Service'));
      return;
    }

    // Location is required for everyone (seekers and employers)
    if (!formData.town) {
      toast.error(t('messages.selectTown') || 'Please select your location');
      return;
    }

    // Validation for job seeker fields (matches signup form exactly)
    if (isWorker) {
      if (!formData.phone || formData.phone.trim() === '') {
        toast.error(t('messages.phoneRequired') || 'Phone number is required');
        return;
      }
      if (!formData.years_of_experience) {
        toast.error(t('messages.experienceRequired') || 'Please select your years of experience');
        return;
      }
      if (!formData.education_level) {
        toast.error(t('messages.educationRequired') || 'Please select your education level');
        return;
      }
      if (!formData.date_of_birth) {
        toast.error(t('messages.dobRequired') || 'Date of birth is required');
        return;
      }
      if (!formData.skills || formData.skills.length === 0) {
        toast.error(t('messages.skillsRequired', 'Please select your skills before proceeding. Tap on the skills that match your experience.'));
        return;
      }
    }

    // Validation for employer fields
    if (isEmployer) {
      if (!formData.business_phone || formData.business_phone.trim() === '') {
        toast.error(t('messages.phoneRequired') || 'Phone number is required');
        return;
      }
      // Private households skip business-specific validation
      if (!formData.is_private_household) {
        if (!formData.business_name) {
          toast.error(t('messages.businessNameRequired') || 'Please enter your business name');
          return;
        }
        if (!formData.industry) {
          toast.error(t('messages.industryRequired') || 'Please select your industry');
          return;
        }
        if (!formData.number_of_employees) {
          toast.error(t('messages.employeeCountRequired') || 'Please select number of employees');
          return;
        }
      }
    }

    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        toast.error('Session expired. Please login again.');
        window.location.href = '/sign-up';
        return;
      }

      // Prepare update data based on role (matches signup form exactly)
      const updateData = {
        town: formData.town,
        terms_agreed_at: profile.terms_agreed_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (isWorker) {
        updateData.phone = formData.phone.trim();
        updateData.years_of_experience = formData.years_of_experience;
        updateData.education_level = formData.education_level;
        updateData.date_of_birth = formData.date_of_birth;
        updateData.skills = formData.skills;
      }

      if (isEmployer) {
        updateData.phone = formData.business_phone.trim();
        updateData.business_phone = formData.business_phone.trim();
        updateData.is_private_household = formData.is_private_household;

        if (formData.is_private_household) {
          // Auto-fill defaults for private households
          const town = formData.town || profile.town || 'Bohol';
          updateData.business_name = `Private Household in ${town}`;
          updateData.industry = 'Private Household';
          updateData.business_type = 'Private Household';
          updateData.number_of_employees = '1-5';
        } else {
          updateData.business_name = formData.business_name.trim();
          updateData.industry = formData.industry;
          if (formData.business_type) {
            updateData.business_type = formData.business_type;
          }
          updateData.number_of_employees = formData.number_of_employees;
        }

        // Save Facebook page link if provided
        if (formData.facebook_page.trim()) {
          updateData.social_media_links = { facebook: formData.facebook_page.trim() };
        }
      }

      // Update profile using the actual profile ID (not auth user ID)
      const { error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', profile.id);

      if (updateError) {
        console.error('Error updating profile:', updateError);
        throw updateError;
      }

      toast.success(t('messages.profileCompleted') || 'Profile completed successfully!');

      // Google/Facebook sign-ups never passed the email form, so THIS is their sign-up
      // conversion: report it server-side (Conversions API) and to the pixel with one shared
      // event id. Skipped for old accounts that merely came back to fill a missing field.
      await reportRegistration({
        token: session.access_token,
        userId: session.user.id,
        role: profile.role,
        createdAt: profile.created_at,
      });

      // Fire-and-forget: check referral payout eligibility
      const rc = localStorage.getItem('referral_code');
      api.post('/api/referrals/check-profile-payout', { referral_code: rc || undefined }, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      }).then(() => { if (rc) localStorage.removeItem('referral_code'); })
        .catch(() => {});

      // This is the last onboarding screen for Google/Facebook sign-ups, so it owns
      // the return trip: send them back to the job they tapped Apply on (single-use,
      // cleared here) instead of stranding them on the generic dashboard.
      const returnTo = takeReturnTo();

      // Small delay to ensure Supabase has propagated the update
      // Then force a page reload to refresh the profile in App.js
      setTimeout(() => {
        window.location.href = returnTo || '/dashboard';
      }, 500);
    } catch (error) {
      console.error('Error completing profile:', error);
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  const isWorker = profile.role === 'job_seeker' || profile.role === 'contractor';
  const isEmployer = profile.role === 'employer' || profile.role === 'owner';

  return (
    <div className="min-h-screen-safe flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex items-center justify-center mb-4">
            <img src={logoUrl} alt="Logo" className="h-16 w-16 object-contain" />
          </div>
          <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
            <CheckCircle size={20} />
            <span className="text-sm font-medium">{t('auth.almostThere') || 'Almost There!'}</span>
          </div>
          <CardTitle className="text-2xl">{t('auth.completeYourProfile') || 'Complete Your Profile'}</CardTitle>
          <CardDescription>
            {t('auth.completeProfileDescription') || 'Just a few more details to get started'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Location — required for everyone (seekers and employers) */}
            <div className="space-y-2">
              <Label htmlFor="town">{t('profile.location') || 'Your Location'} *</Label>
              <Select value={formData.town} onValueChange={(value) => handleInputChange('town', value)} required>
                <SelectTrigger id="town">
                  <SelectValue placeholder={t('post.selectLocation') || 'Select your town'} />
                </SelectTrigger>
                <SelectContent>
                  {BOHOL_TOWNS.map((town) => (
                    <SelectItem key={town} value={town}>{town}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Job Seeker / Contractor Fields */}
            {isWorker && (
              <>
                {/* Optional resume upload — pre-fills the form below.
                    Skip is OK: user can still fill the form manually. */}
                <ResumeUploadCard
                  existingFileUrl={profile?.resume_file_url || null}
                  onParsed={handleResumeParsed}
                  autoParseAfterUpload={true}
                  signupMode={true}
                />

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                    {t('addSkills.orPickManually', 'or fill out the form')}
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('profile.phone') || 'Phone Number'} *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="09XX XXX XXXX"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="years_of_experience">{t('profile.yearsOfExperience') || 'Years of Experience'} *</Label>
                  <Select value={formData.years_of_experience} onValueChange={(value) => handleInputChange('years_of_experience', value)} required>
                    <SelectTrigger>
                      <SelectValue placeholder={t('profile.selectExperience') || 'Select experience level'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="less_than_1">{t('profile.lessThan1Year') || 'Less than 1 year'}</SelectItem>
                      <SelectItem value="1_to_3">{t('profile.1to3Years') || '1-3 years'}</SelectItem>
                      <SelectItem value="3_to_5">{t('profile.3to5Years') || '3-5 years'}</SelectItem>
                      <SelectItem value="5_to_10">{t('profile.5to10Years') || '5-10 years'}</SelectItem>
                      <SelectItem value="10_plus">{t('profile.10PlusYears') || '10+ years'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="education_level">{t('profile.educationLevel') || 'Education Level'} *</Label>
                  <Select value={formData.education_level} onValueChange={(value) => handleInputChange('education_level', value)} required>
                    <SelectTrigger>
                      <SelectValue placeholder={t('profile.selectEducation') || 'Select education level'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="elementary">{t('profile.elementary') || 'Elementary'}</SelectItem>
                      <SelectItem value="high_school">{t('profile.highSchool') || 'High School'}</SelectItem>
                      <SelectItem value="vocational">{t('profile.vocational') || 'Vocational'}</SelectItem>
                      <SelectItem value="college_level">{t('profile.collegeLevel') || 'College Level'}</SelectItem>
                      <SelectItem value="college_graduate">{t('profile.collegeGraduate') || 'College Graduate'}</SelectItem>
                      <SelectItem value="bachelors">{t('profile.bachelors') || "Bachelor's Degree"}</SelectItem>
                      <SelectItem value="masters">{t('profile.masters') || "Post Graduate"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date_of_birth">{t('profile.dateOfBirth') || 'Date of Birth'} *</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                    min="1925-01-01"
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split('T')[0]}
                    required
                  />
                </div>

                {/* Skills Selection */}
                <div className="space-y-2">
                  <Label>{t('profile.skills', 'Your Skills')} *</Label>
                  <p className="text-xs text-muted-foreground">{t('auth.selectSkillsHint', 'Select skills that match your experience (at least 1)')}</p>
                  <SkillsSelector
                    options={WORKER_SKILLS}
                    value={formData.skills}
                    onChange={(skills) => handleInputChange('skills', skills)}
                    max={12}
                  />
                </div>
              </>
            )}

            {/* Employer / Owner Fields */}
            {isEmployer && (
              <>
                {/* Employer type selection */}
                <div className="space-y-2">
                  <Label>{t('auth.employerType', 'What type of employer are you?')}</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => handleInputChange('is_private_household', false)}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                        !formData.is_private_household
                          ? 'border-primary bg-primary/5'
                          : 'border-muted hover:border-muted-foreground/30'
                      }`}
                    >
                      <Briefcase size={20} />
                      <span className="text-sm font-medium">{t('auth.business', 'Business')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, is_private_household: true, business_name: '', industry: '', business_type: '', number_of_employees: '' }))}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                        formData.is_private_household
                          ? 'border-primary bg-primary/5'
                          : 'border-muted hover:border-muted-foreground/30'
                      }`}
                    >
                      <Home size={20} />
                      <span className="text-sm font-medium">{t('auth.privateHousehold', 'Private Household')}</span>
                    </button>
                  </div>
                </div>

                {formData.is_private_household ? (
                  /* Private household — minimal fields */
                  <div className="rounded-lg bg-muted/50 p-3">
                    <p className="text-sm text-muted-foreground">
                      {t('auth.privateHouseholdNote', 'Your listing will appear as "Private Household in [your municipality]". Perfect for hiring nannies, kasambahay, drivers, caretakers, etc.')}
                    </p>
                  </div>
                ) : (
                  /* Business employer — full fields */
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="business_name">{t('auth.businessName') || 'Business Name'} *</Label>
                      <Input
                        id="business_name"
                        placeholder="ABC Company Inc."
                        value={formData.business_name}
                        onChange={(e) => handleInputChange('business_name', e.target.value)}
                        required
                      />
                      <p className="text-xs text-muted-foreground">This is the name shown publicly to job seekers.</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="industry">{t('auth.industry') || 'Industry'} *</Label>
                      <Select value={formData.industry} onValueChange={(value) => handleInputChange('industry', value)} required>
                        <SelectTrigger>
                          <SelectValue placeholder={t('auth.selectIndustry') || 'Select industry'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Agriculture">Agriculture</SelectItem>
                          <SelectItem value="Construction">Construction</SelectItem>
                          <SelectItem value="Education">Education</SelectItem>
                          <SelectItem value="Food & Beverage">Food & Beverage</SelectItem>
                          <SelectItem value="Healthcare">Healthcare</SelectItem>
                          <SelectItem value="Hospitality & Tourism">Hospitality & Tourism</SelectItem>
                          <SelectItem value="IT & Technology">IT & Technology</SelectItem>
                          <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                          <SelectItem value="Retail">Retail</SelectItem>
                          <SelectItem value="Transportation">Transportation</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="business_type">{t('auth.businessType') || 'Business Type'}</Label>
                      <Select value={formData.business_type} onValueChange={(value) => handleInputChange('business_type', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('auth.selectBusinessType') || 'Select business type'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Sole Proprietorship">Sole Proprietorship</SelectItem>
                          <SelectItem value="Partnership">Partnership</SelectItem>
                          <SelectItem value="Corporation">Corporation</SelectItem>
                          <SelectItem value="Cooperative">Cooperative</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="number_of_employees">{t('auth.numberOfEmployees') || 'Number of Employees'} *</Label>
                      <Select value={formData.number_of_employees} onValueChange={(value) => handleInputChange('number_of_employees', value)} required>
                        <SelectTrigger>
                          <SelectValue placeholder={t('auth.selectEmployeeCount') || 'Select employee count'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-5">1-5 employees</SelectItem>
                          <SelectItem value="6-10">6-10 employees</SelectItem>
                          <SelectItem value="11-50">11-50 employees</SelectItem>
                          <SelectItem value="51-100">51-100 employees</SelectItem>
                          <SelectItem value="101-500">101-500 employees</SelectItem>
                          <SelectItem value="500+">500+ employees</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="facebook_page">{t('auth.facebookPage', 'Facebook Page')} <span className="text-muted-foreground text-xs">({t('common.optional', 'optional')})</span></Label>
                  <Input
                    id="facebook_page"
                    type="url"
                    placeholder="https://facebook.com/yourbusiness"
                    value={formData.facebook_page}
                    onChange={(e) => handleInputChange('facebook_page', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{t('auth.facebookPageHint', 'Link to your business Facebook page — shown on your employer profile.')}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_phone">{t('auth.businessPhone') || 'Business Phone'} *</Label>
                  <Input
                    id="business_phone"
                    type="tel"
                    placeholder="09XX XXX XXXX"
                    value={formData.business_phone}
                    onChange={(e) => handleInputChange('business_phone', e.target.value)}
                    required
                  />
                </div>
              </>
            )}

            {/* Privacy Policy and Terms Agreement */}
            <div className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <Checkbox
                id="terms-agreement"
                checked={termsAgreed}
                onCheckedChange={(checked) => setTermsAgreed(checked)}
                className="mt-0.5"
              />
              <label htmlFor="terms-agreement" className="text-sm leading-relaxed cursor-pointer">
                <span className="text-blue-900 dark:text-blue-100">
                  {t('auth.agreeToTerms', 'I have read and agree to the')}{' '}
                </span>
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                  {t('footer.privacy', 'Privacy Policy')}
                </a>{' '}
                <span className="text-blue-900 dark:text-blue-100">{t('common.and', 'and')}</span>{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                  {t('footer.terms', 'Terms of Service')}
                </a>
              </label>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-green-600 hover:bg-green-700"
                disabled={loading || !termsAgreed}
              >
                {loading ? t('common.loading') : (t('auth.continueButton') || 'Continue to Dashboard')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileCompletion;
